import type { Range } from "@tiptap/core"
import type { Editor } from "@tiptap/react"
import { LuCode, LuImage, LuPaperclip } from "react-icons/lu"
import type { CommandItem } from "../../chakra/rich-text-editor-menu"
import type { InsertionToolbarOption } from "../types"

const SLASH_ITEM_META: Record<
  InsertionToolbarOption,
  {
    label: string
    description: string
    icon: CommandItem["icon"]
    terms: string[]
  }
> = {
  image: {
    label: "Image",
    description: "Insert an inline image",
    icon: LuImage,
    terms: ["image", "photo", "picture", "media", "upload"]
  },
  attachment: {
    label: "Attachment",
    description: "Insert a file attachment",
    icon: LuPaperclip,
    terms: ["attachment", "file", "document", "upload"]
  },
  embed: {
    label: "Embed",
    description: "Embed external content",
    icon: LuCode,
    terms: ["embed", "iframe", "video", "url", "code"]
  }
}

function matchesSlashQuery(
  option: InsertionToolbarOption,
  query: string
): boolean {
  if (!query) return true
  const config = SLASH_ITEM_META[option]
  const terms = [config.label, config.description, ...config.terms]
  return terms.some((term) => term.toLowerCase().includes(query))
}

export type SlashInsertionItem = CommandItem & {
  execute: (ctx: { editor: Editor; range: Range }) => void
}

export type SlashInsertionActions = {
  insertionOptions: InsertionToolbarOption[]
  triggerImage: () => void
  triggerAttachment: () => void
  openEmbedComposer: () => void
}

export function buildSlashInsertionItems(
  query: string,
  actions: SlashInsertionActions
): SlashInsertionItem[] {
  const q = query.toLowerCase()
  return actions.insertionOptions
    .filter((option) => matchesSlashQuery(option, q))
    .map((option) => {
      const meta = SLASH_ITEM_META[option]
      return {
        id: option,
        label: meta.label,
        description: meta.description,
        icon: meta.icon,
        execute: ({ editor, range }) => {
          editor.chain().focus().deleteRange(range).run()
          if (option === "image") {
            requestAnimationFrame(() => actions.triggerImage())
            return
          }
          if (option === "attachment") {
            requestAnimationFrame(() => actions.triggerAttachment())
            return
          }
          requestAnimationFrame(() => actions.openEmbedComposer())
        }
      }
    })
}
